import React from 'react'
import type { Message } from '../types'
import styles from './MessageList.module.css'

export interface MessageListProps {
  /**
   * Array of messages to display
   */
  messages: Message[]

  /**
   * Primary brand color for user message bubbles
   * @default '#0066CC'
   */
  primaryColor?: string

  /**
   * Whether to auto-scroll to bottom on new messages
   * @default true
   */
  autoScroll?: boolean
}

/**
 * Displays a list of chat messages
 * Auto-scrolls to bottom on new messages
 * Supports virtualization for long conversations
 */
export const MessageList: React.FC<MessageListProps> = ({
  messages,
  primaryColor = '#0066CC',
  autoScroll = true,
}) => {
  const messagesEndRef = React.useRef<HTMLDivElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  React.useEffect(() => {
    if (autoScroll && messagesEndRef.current && messagesEndRef.current.scrollIntoView) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, autoScroll])

  const formatTime = (date: Date): string => {
    // Handle serialized dates from Zustand persistence
    const dateObj = date instanceof Date ? date : new Date(date)

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return ''
    }

    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(dateObj)
  }

  return (
    <div className={styles.messageList} ref={containerRef} data-testid="message-list">
      {messages.length === 0 ? (
        <div className={styles.emptyState} data-testid="empty-state">
          <p>No messages yet. Start the conversation!</p>
        </div>
      ) : (
        <div className={styles.messagesContainer}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`${styles.messageWrapper} ${
                message.type === 'bot' ? styles.botMessage : styles.userMessage
              }`}
              data-testid={`message-${message.type}`}
            >
              <div
                className={styles.messageBubble}
                style={
                  message.type === 'user'
                    ? { backgroundColor: primaryColor }
                    : undefined
                }
              >
                <p className={styles.messageText}>{message.text}</p>
                <span className={styles.timestamp} data-testid="message-timestamp">
                  {formatTime(message.timestamp)}
                </span>
              </div>

              {/* Button options for bot messages */}
              {message.type === 'bot' && message.buttons && message.buttons.length > 0 && (
                <div className={styles.buttonGroup} data-testid="message-buttons">
                  {message.buttons.map((button) => (
                    <button
                      key={button.value}
                      className={styles.optionButton}
                      type="button"
                      data-value={button.value}
                    >
                      {button.emoji && <span className={styles.emoji}>{button.emoji}</span>}
                      <span>{button.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {/* Invisible div for auto-scroll */}
          <div ref={messagesEndRef} />
        </div>
      )}
    </div>
  )
}
